import cloneDeep from 'clone-deep'
import { COMPANY_DNS } from 'config'
import type { InputType } from 'config/input.type.constants'
import { feasibilityAllKeys, feasibilityKeys } from 'pages/Loan'
import { defaultInputs } from 'pages/Loan/OrderAppraisals/constants'

/**
 * The status on an extension carried over from a prior servicer's system. Neither paid nor
 * requested here, so it is deliberately none of the Stripe-owned statuses and the grid renders it
 * as plain text rather than a control. Mirrors `IMPORTED_EXTENSION_STATUS` on the backend.
 */
export const IMPORTED_EXTENSION_STATUS = 'Imported'

/**
 * An extension whose fee request never went out, because it had no recipient or the send failed.
 * Mirrors `EXTENSION_NOT_SENT_STATUS` on the backend. Like `Imported` it is none of the
 * Stripe-owned statuses, so a payment that arrives later still takes the row over.
 */
export const NOT_SENT_EXTENSION_STATUS = 'Not Sent'

/**
 * An extension saved with no fee, which the tenant allows under Extension Defaults. There is no fee
 * request to send or pay. Mirrors `EXTENSION_NO_FEE_STATUS` on the backend.
 */
export const NO_FEE_EXTENSION_STATUS = 'No Fee'

export const extensionInputLogic = (_data: any) => {
  let data = cloneDeep(_data)

  feasibilityKeys.forEach((key) => {
    if (!!data[key]) data[key].visible = true
  })
  return data
}

/**
 * Slots 3 and 4 are prefilled only where the loan genuinely has that many borrowers, which is
 * rare; their usual job is a tiered entity, where the members signing for the borrowing LLC are
 * people (or entities) the loan does not hold as borrower records at all.
 */
const SIGNER_SLOT_TOOLTIP =
  'Leave blank unless the agreement needs a third or fourth signature line, for example a tiered entity whose members sign for the borrowing company. A blank slot prints no signature block on templates that have been set up for it.'

/**
 * The extension-agreement terms and signature block. These are the values The Mortgage Office
 * kept as per-loan "Extension" custom fields; here they live on each extension so a loan can
 * carry a real history of them. Every one is prefilled from loan data and stays editable.
 */
export const extensionDocumentInputs = (): Record<string, InputType> => ({
  extensionNumber: {
    inputType: 'text',
    title: 'Extension Number',
    span: 1,
    visible: true,
    required: false,
    tooltip: 'Prefilled with the next number. Set it manually on a loan whose earlier extensions were imported.',
  },
  extensionDate: {
    inputType: 'text',
    type: 'date',
    title: 'Extension Date',
    span: 1,
    visible: true,
    required: false,
  },
  originalMaturityDate: {
    inputType: 'text',
    type: 'date',
    title: 'Original Maturity Date',
    span: 1,
    visible: true,
    required: false,
  },
  currentMaturityDate: {
    inputType: 'text',
    type: 'date',
    title: 'Current Maturity Date',
    span: 1,
    visible: true,
    required: false,
  },
  newMaturityDate: {
    inputType: 'text',
    type: 'date',
    title: 'New Maturity Date',
    span: 1,
    visible: true,
    required: false,
  },
  documentTemplate: {
    inputType: 'select',
    title: 'Extension Document',
    span: 3,
    visible: true,
    required: false,
    hasDefaultOption: true,
    options: {},
  },
  vesting: {
    inputType: 'text',
    title: 'Vesting',
    span: 4,
    visible: true,
    required: false,
  },
  borrower1Name: {
    inputType: 'text',
    title: 'Borrower 1 Signer',
    span: 1,
    visible: true,
    required: false,
  },
  borrower1Title: {
    inputType: 'text',
    title: 'Borrower 1 Title',
    span: 1,
    visible: true,
    required: false,
  },
  borrower2Name: {
    inputType: 'text',
    title: 'Borrower 2 Signer',
    span: 1,
    visible: true,
    required: false,
  },
  borrower2Title: {
    inputType: 'text',
    title: 'Borrower 2 Title',
    span: 1,
    visible: true,
    required: false,
  },
  borrower3Name: {
    inputType: 'text',
    title: 'Borrower 3 Signer',
    span: 1,
    visible: true,
    required: false,
    tooltip: SIGNER_SLOT_TOOLTIP,
  },
  borrower3Title: {
    inputType: 'text',
    title: 'Borrower 3 Title',
    span: 1,
    visible: true,
    required: false,
  },
  borrower4Name: {
    inputType: 'text',
    title: 'Borrower 4 Signer',
    span: 1,
    visible: true,
    required: false,
    tooltip: SIGNER_SLOT_TOOLTIP,
  },
  borrower4Title: {
    inputType: 'text',
    title: 'Borrower 4 Title',
    span: 1,
    visible: true,
    required: false,
  },
  lenderSigner: {
    inputType: 'text',
    title: 'Lender Signer',
    span: 1,
    visible: true,
    required: false,
  },
  lenderTitle: {
    inputType: 'text',
    title: 'Lender Title',
    span: 1,
    visible: true,
    required: false,
  },
})

export const loanExtensionInputs = (customer: boolean = false) => {
  const inputs = defaultInputs()
  const filteredInputs: Record<string, InputType> = {}

  if (!customer) Object.assign(filteredInputs, extensionDocumentInputs())

  filteredInputs['documents'] = {
    inputType: 'FileTable',
    title: 'Please upload loan extension documents.',
    value: [],
    error: '',
    span: 4,
    filePath: '',
    required: false,
    showCategory: false,
    showStatus: false,
  }

  filteredInputs['carrier'] = {
    inputType: 'text',
    title: 'Carrier',
    value: '',
    span: 2,
    visible: true,
    required: false,
    disabled: true,
  }
  filteredInputs['trackingNumber'] = {
    inputType: 'text',
    title: 'Shipping/Tracking Number',
    value: '',
    span: 2,
    visible: true,
    required: false,
    disabled: true,
  }

  filteredInputs.paymentAmount = {
    ...inputs['paymentAmount'],
    title: 'Extension Fee',
    visible: true,
    required: true,
    prefix: '$',
    span: 4,
  }

  feasibilityAllKeys.forEach((key) => {
    filteredInputs[key] = inputs[key]
    if (customer) {
    } else {
      if (!['paymentAmount', 'feasibilityEmailToLink'].includes(key)) filteredInputs[key].required = false
    }
  })

  delete filteredInputs['feasibilityTitle']
  delete filteredInputs['feasibilityACHPaymentAmount']
  delete filteredInputs['feasibilityCCStudyFee']

  filteredInputs['feasibilityEmailToLink'] = {
    inputType: 'email',
    title: 'Email to Send Link',
    required: true,
    visible: false,
    span: 2,
    defaultEmails: [
      {
        party: 'Servicing',
        email: `kfry@${COMPANY_DNS}`,
      },
    ],
  }

  if (!customer) {
    filteredInputs['notes'] = {
      inputType: 'textarea',
      title: 'Notes',
      rows: 0,
      value: '',
      span: 4,
      visible: true,
      required: false,
    }
  }

  if (customer) {
    filteredInputs.paymentAmount.disabled = true
    filteredInputs.feasibilityEmailToLink.disabled = true
    filteredInputs.carrier.disabled = false
    filteredInputs.trackingNumber.disabled = false
    // filteredInputs.carrier.required = true
    // filteredInputs.trackingNumber.required = true
  }

  return filteredInputs
}
